import { updateUserName } from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { DataUserTct } from '@dealstack/shared/lib/generated';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

export enum USER_NAME_FORM {
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
}

interface IUserInfoForm {
	[USER_NAME_FORM.FIRST_NAME]: string;
	[USER_NAME_FORM.LAST_NAME]: string;
}

export const useNameFormController = (refreshData: () => void, user?: DataUserTct) => {
	const toastAPI = useDSToast();

	const updateUserNameRequest = useMutation({
		mutationFn: updateUserName,
	});

	const updateNameForm = useForm<IUserInfoForm>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			[USER_NAME_FORM.FIRST_NAME]: user?.firstName ?? '',
			[USER_NAME_FORM.LAST_NAME]: user?.lastName ?? '',
		},
	});

	useEffect(() => {
		if (!user?.firstName && !user?.lastName) {
			return;
		}
		updateNameForm.reset({
			[USER_NAME_FORM.FIRST_NAME]: user.firstName,
			[USER_NAME_FORM.LAST_NAME]: user.lastName,
		});
	}, [user?.firstName, user?.lastName]);

	const onNameUpdateSubmit = useCallback((formValues: IUserInfoForm) => {
		if (!user?.id) {
			return;
		}
		updateUserNameRequest.mutate(
			{
				userId: user.id,
				body: formValues,
			},
			{
				onError: (error) => {
					logger.error(error);
					toastAPI.error({
						title: 'Error',
						message: "Data couldn't be updated!",
					});
				},
				onSuccess: () => {
					refreshData();
					toastAPI.success({
						title: 'Success',
						message: 'Successfully updated!',
					});
				},
			}
		);
	}, []);

	return {
		updateNameForm,
		onNameUpdateSubmit,
	};
};
